<template>
  <section class="infra-page" :dir="isArabic ? 'rtl' : 'ltr'">
    <div v-if="loading" class="page-loading">
      <div class="loader-card">
        <div class="loader-ring"></div>

        <h3>{{ tr("loadingTitle") }}</h3>

        <p>{{ tr("loadingText") }}</p>
      </div>
    </div>

    <div class="page-header">
      <h1>{{ tr("pageTitle") }}</h1>

      <p>{{ tr("pageSubtitle") }}</p>
    </div>

    <div class="axis-block">
      <div class="axis-summary">
        <div class="axis-summary-head">
          <div class="axis-title">
            <h2>
              {{ tr("infrastructure") }}

              <small>(INF)</small>
            </h2>

            <p>{{ tr("pageSubtitle") }}</p>
          </div>

          <div class="axis-weight">
            <strong>
              {{ axisAverage !== null ? `${axisAverage}%` : "-" }}
            </strong>

            <small>
              {{ tr("fisWeight") }}:

              {{ axisWeight !== null ? `${axisWeight}%` : "-" }}
            </small>
          </div>
        </div>

        <div class="axis-statistics">
          <div class="axis-stat-card">
            <strong>{{ infraKpis.length }}</strong>

            <span>{{ tr("indicatorsCount") }}</span>
          </div>

          <div class="axis-stat-card">
            <strong>{{ facilities.length }}</strong>

            <span>{{ tr("sample") }} / {{ tr("facilitiesUnit") }}</span>
          </div>

          <div class="axis-stat-card">
            <strong>{{ infraCriticalCount }}</strong>

            <span>{{ tr("criticalIndicators") }}</span>
          </div>
        </div>
      </div>

      <div class="indicator-grid">
        <article
          v-for="kpi in infraKpis"
          :key="kpi.code"
          class="indicator-card"
          :class="{ flag: kpiStatus(kpi) === 'r' }"
          :data-critical-label="tr('criticalFlag')"
        >
          <div class="indicator-top">
            <span class="code">{{ kpi.code }}</span>

            <span v-if="kpi.composite" class="composite">{{
              tr("composite")
            }}</span>
          </div>

          <h3>{{ kpi.name }}</h3>

          <div class="indicator-value">
            <strong :style="{ color: statusColor(kpi) }">
              {{ kpi.val }}
            </strong>

            <span>{{ kpi.unit }}</span>

            <em
              >{{ tr("standard") }} {{ kpi.dir === "lt" ? "≤" : "≥" }}

              {{ kpi.std }}</em
            >
          </div>

          <div class="gauge">
            <div
              :style="{
                width: barWidth(kpi) + '%',

                background: statusColor(kpi),
              }"
            ></div>

            <i :style="thresholdStyle(kpi)"></i>
          </div>

          <div class="indicator-foot">
            <span>
              {{ tr("achievement") }}:

              <b :style="{ color: statusColor(kpi) }">{{ achieve(kpi) }}%</b>
            </span>

            <span :class="['rag', kpiStatus(kpi)]">
              {{ statusLabel[kpiStatus(kpi)] }}
            </span>
          </div>

          <div v-if="kpi.chart" class="mini-chart">
            <h4>{{ kpi.chart.label }}</h4>

            <apexchart
              v-if="chartsReady"
              :type="kpiApex(kpi).type"
              height="250"
              :series="kpiApex(kpi).series"
              :options="kpiApex(kpi).options"
            />
          </div>
        </article>
      </div>
    </div>

    <div class="charts-grid">
      <div class="chart-card">
        <div class="section-title">{{ tr("priorityDistribution") }}</div>

        <apexchart
          v-if="chartsReady"
          type="donut"
          height="320"
          :series="prioritySeries"
          :options="priorityOptions"
        />
      </div>

      <div class="chart-card">
        <div class="section-title">
          {{ tr("fisByFacility") }}
        </div>

        <apexchart
          v-if="chartsReady"
          type="bar"
          height="320"
          :series="fisSeries"
          :options="fisOptions"
        />
      </div>

      <div class="chart-card">
        <div class="section-title">{{ tr("damageDistribution") }}</div>

        <apexchart
          v-if="chartsReady"
          type="donut"
          height="320"
          :series="damageChartSeries"
          :options="damageChartOptions"
        />
      </div>

      <div class="chart-card">
        <div class="section-title">{{ tr("buildingType") }}</div>

        <apexchart
          v-if="chartsReady"
          type="bar"
          height="320"
          :series="buildingTypeSeries"
          :options="buildingTypeOptions"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { useI18n } from "vue-i18n";

import api from "@/plugins/api";

const { t, locale } = useI18n();

const isArabic = computed(() => locale.value === "ar");

const tr = (key, params = {}) => t(`infrastructurePage.${key}`, params);

const loading = ref(false);

const chartsReady = ref(false);

const rawFacilities = ref([]);

const buildings = ref([]);

const fsciIndicator = ref({ summary: {}, by_governorate: [], data: [] });

const fsciFacilities = ref([]);

const inf05Equipment = ref({ summary: {}, by_governorate: [] });

const inf07Electricity = ref({ summary: {}, by_governorate: [] });

const inf08Energy = ref({ summary: {}, components: [] });

const inf10Access = ref({ summary: { INF_10: 0 }, data: [] });

const womenSafetyPrivacy = ref({ summary: { INF_11: 0 }, data: [] });

const charts = ref({
  damage_levels: {},

  service_risks: {},

  building_types: {},

  structural_conditions: {},
});

const statusLabel = computed(() => ({
  g: tr("compliant"),

  a: tr("near"),

  r: tr("gap"),
}));

const colors = { g: "#10b981", a: "#f59e0b", r: "#ef4444" };

const safeText = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;

  return String(value);
};

const normalizeFacility = (item, index) => {
  const facilityName = isArabic.value
    ? safeText(
        item.facility_name_ar || item.facility_name_en || item.facility_name
      )
    : safeText(
        item.facility_name_en || item.facility_name_ar || item.facility_name
      );

  const facilityType = isArabic.value
    ? safeText(
        item.facility_type_ar || item.facility_type_en || item.facility_type
      )
    : safeText(
        item.facility_type_en || item.facility_type_ar || item.facility_type
      );

  const governorate = isArabic.value
    ? safeText(
        item.governorate_name_ar || item.governorate_name_en || item.governorate
      )
    : safeText(
        item.governorate_name_en || item.governorate_name_ar || item.governorate
      );

  const district = isArabic.value
    ? safeText(item.district_name_ar || item.district_name_en || item.district)
    : safeText(item.district_name_en || item.district_name_ar || item.district);

  const operationalStatus = isArabic.value
    ? safeText(
        item.operational_status_ar ||
          item.operational_status_en ||
          item.operational_status
      )
    : safeText(
        item.operational_status_en ||
          item.operational_status_ar ||
          item.operational_status
      );

  return {
    ...item,

    id: item.survey_id || item.id || index + 1,

    survey_id: item.survey_id || item.id || index + 1,

    facility_name: facilityName,

    facility_type: facilityType,

    governorate,

    district,

    operational_status: operationalStatus,

    total_staff: Number(item.cadre_total_count ?? item.total_staff ?? 0),

    beneficiaries_month: Number(
      item.beneficiaries_current ?? item.beneficiaries_month ?? 0
    ),

    target_population: Number(item.target_population ?? "-"),

    FIS: Number(item.FIS_result ?? item.FIS ?? item.fis ?? "-"),

    periority: item.priority_code || item.periority || item.priority || "P4",
  };
};

const facilities = computed(() =>
  rawFacilities.value.map((item, index) => normalizeFacility(item, index))
);

const cleanKey = (value) => {
  if (value === null || value === undefined || value === "")
    return tr("unspecified");

  return String(value);
};

const cleanChartObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};

  const result = {};

  Object.entries(obj).forEach(([key, value]) => {
    const safeKey = cleanKey(key);

    const safeValue = Number(value || 0);

    if (safeValue > 0) result[safeKey] = safeValue;
  });

  return result;
};

const mapLabels = (obj, labelsMap) => {
  const keys = Object.keys(cleanChartObject(obj));

  if (!keys.length) return [tr("noData")];

  return keys.map((key) => labelsMap[key] || key);
};

const mapSeries = (obj) => {
  const values = Object.values(cleanChartObject(obj)).map((v) =>
    Number(v || 0)
  );

  return values.length ? values : [0];
};

const normalizeBuilding = (item) => ({
  id: item?.id,

  name: cleanKey(
    item?.build_name_ar || item?.build_name_en || item?.name || tr("unnamed")
  ),

  build_type: cleanKey(item?.build_type),

  structure_status: cleanKey(item?.structure_status),

  overall_severity: cleanKey(
    item?.overall_damage_level || item?.overall_severity
  ),

  service_disruption_risk: cleanKey(
    item?.service_disruption_risk || item?.risk_level || item?.service_risk
  ),

  rehabilitation_cost: Number(
    item?.rehabilitation_cost ||
      item?.rehab_cost ||
      item?.estimated_rehabilitation_cost ||
      0
  ),
});

const normalizeAccessIndex = (payload) => ({
  summary: {
    INF_10: Number(
      payload?.average ??
        payload?.summary?.INF_10 ??
        payload?.summary?.access_score ??
        0
    ),
  },

  data: Array.isArray(payload?.by_points)
    ? payload.by_points
    : Array.isArray(payload?.data)
    ? payload.data
    : [],
});

const normalizeWomenSafety = (payload) => ({
  summary: {
    INF_11: Number(payload?.average || payload?.summary?.INF_11 || 0),
  },

  data: payload?.by_dimensions || payload?.data || [],
});

const firstList = (...lists) =>
  lists.find((list) => Array.isArray(list) && list.length) || [];

const fetchFacilities = async () => {
  const { data } = await api.get("/health-facilities");

  rawFacilities.value = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
    ? data
    : [];
};

const fetchBuildings = async () => {
  const { data } = await api.get("/buildings");

  const list = Array.isArray(data) ? data : data?.data || [];

  buildings.value = list.map(normalizeBuilding);
};

const fetchCharts = async () => {
  const { data } = await api.get("/buildings/charts");

  charts.value = {
    damage_levels: cleanChartObject(data?.damage_levels),

    service_risks: cleanChartObject(data?.service_risks),

    building_types: cleanChartObject(data?.building_types),

    structural_conditions: cleanChartObject(data?.structural_conditions),
  };
};

const fetchIndicators = async () => {
  const [
    fsciRes,

    fsciFacilitiesRes,

    inf05Res,

    inf07Res,

    inf08Res,

    inf10Res,

    womenSafetyRes,
  ] = await Promise.all([
    api.get("/health-facilities/fsci-by-governorate"),

    api.get("/health-facilities/fsci"),

    api.get("/health-facilities/equipmentFunctionality"),

    api.get("/health-facilities/electricityAvailability"),

    api.get("/health-facilities/energyReliability"),

    api.get("/health-facilities/accessIndex"),

    api.get("/health-facilities/womenSafetyPrivacyIndex"),
  ]);

  fsciIndicator.value = fsciRes.data || {
    summary: {},

    by_governorate: [],

    data: [],
  };

  const fsciPayload = fsciFacilitiesRes.data;

  fsciFacilities.value = Array.isArray(fsciPayload?.data)
    ? fsciPayload.data
    : Array.isArray(fsciPayload)
    ? fsciPayload
    : [];

  inf05Equipment.value = {
    summary: {
      INF_05: Number(
        inf05Res.data?.average || inf05Res.data?.summary?.INF_05 || 0
      ),
    },

    by_governorate: inf05Res.data?.by_governorate || [],
  };

  inf07Electricity.value = {
    summary: {
      INF_07: Number(
        inf07Res.data?.average || inf07Res.data?.summary?.INF_07 || 0
      ),
    },

    by_governorate: inf07Res.data?.by_governorate || [],
  };

  inf08Energy.value = {
    summary: {
      INF_08: Number(
        inf08Res.data?.average || inf08Res.data?.summary?.INF_08 || 0
      ),
    },

    components: inf08Res.data?.components || [],
  };

  inf10Access.value = normalizeAccessIndex(inf10Res.data);

  womenSafetyPrivacy.value = normalizeWomenSafety(womenSafetyRes.data);
};

const axisWeight = ref(null);

const axisAverage = ref(null);

const loadAxisWeight = async () => {
  try {
    const response = await api.get("/health-facilities/axis-averages");

    const payload = response.data?.data;

    axisWeight.value = payload?.infrastructure?.weight ?? null;

    axisAverage.value = payload?.infrastructure?.average ?? null;
  } catch (error) {
    console.error("خطأ في تحميل بيانات المحاور:", error);

    axisWeight.value = null;

    axisAverage.value = null;
  }
};

const loadData = async () => {
  try {
    loading.value = true;

    chartsReady.value = false;

    await Promise.all([
      fetchFacilities(),

      fetchBuildings(),

      fetchCharts(),

      fetchIndicators(),

      loadAxisWeight(),
    ]);
  } catch (error) {
    console.error("Infrastructure Page Error:", error);
  } finally {
    chartsReady.value = true;

    loading.value = false;
  }
};

onMounted(loadData);

const prioritySeries = computed(() => {
  const counts = { P1: 0, P2: 0, P3: 0, P4: 0 };

  facilities.value.forEach((x) => {
    if (counts[x.periority] !== undefined) counts[x.periority]++;
  });

  return Object.values(counts);
});

const priorityOptions = computed(() => ({
  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  labels: [tr("p1Critical"), tr("p2High"), tr("p3Medium"), tr("p4Low")],

  legend: { position: "bottom" },

  dataLabels: { enabled: true },
}));

const fisSeries = computed(() => [
  {
    name: "FSCI",

    data: fsciFacilities.value.map((x) => Number(x.fsci_pct ?? 0)),
  },
]);

const fisOptions = computed(() => ({
  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  xaxis: {
    categories: fsciFacilities.value.map((x) => {
      const facilityName = isArabic.value
        ? x.facility_name_ar || x.facility_name_en || x.facility_name
        : x.facility_name_en || x.facility_name_ar || x.facility_name;

      return facilityName || tr("unspecified");
    }),

    labels: {
      rotate: -45,

      trim: true,

      hideOverlappingLabels: false,
    },
  },

  yaxis: {
    min: 0,

    max: 100,

    tickAmount: 5,

    labels: {
      formatter: (value) => `${Number(value).toFixed(0)}%`,
    },
  },

  tooltip: {
    y: {
      formatter: (value) => `${Number(value).toFixed(1)}%`,
    },
  },

  plotOptions: {
    bar: {
      borderRadius: 6,

      columnWidth: "45%",
    },
  },

  dataLabels: { enabled: false },

  noData: { text: tr("noData") },
}));

const damageLabelsMap = computed(() => ({
  minor: tr("minor"),

  moderate: tr("moderate"),

  severe: tr("severe"),

  major: tr("major"),

  no_damage: tr("noDamage"),

  [tr("unspecified")]: tr("unspecified"),
}));

const buildingTypeLabelsMap = computed(() => ({
  reinforced_concrete: tr("reinforcedConcrete"),

  stone: tr("stone"),

  mixed: tr("mixed"),

  [tr("unspecified")]: tr("unspecified"),
}));

const baseChartOptions = {
  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",
  },

  dataLabels: { enabled: true },

  legend: { position: "bottom" },

  noData: { text: tr("noData") },
};

const miniBaseChart = {
  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",

    foreColor: "#7890ad",
  },

  dataLabels: { enabled: false },

  legend: { position: "bottom" },

  grid: { borderColor: "#d7e0eb" },

  noData: { text: tr("noData") },
};

const damageChartSeries = computed(() => mapSeries(charts.value.damage_levels));

const damageChartOptions = computed(() => ({
  ...baseChartOptions,

  labels: mapLabels(charts.value.damage_levels, damageLabelsMap.value),
}));

const buildingTypeSeries = computed(() => [
  {
    name: tr("buildingCount"),

    data: mapSeries(charts.value.building_types),
  },
]);

const buildingTypeOptions = computed(() => ({
  ...baseChartOptions,

  xaxis: {
    categories: mapLabels(
      charts.value.building_types,

      buildingTypeLabelsMap.value
    ),
  },

  plotOptions: {
    bar: {
      borderRadius: 6,

      columnWidth: "45%",
    },
  },
}));

const fsciGovernorates = computed(() =>
  firstList(fsciIndicator.value.by_governorate, fsciIndicator.value.data)
);

const infraCharts = computed(() => ({
  "INF-01": {
    type: "bar",

    label: tr("fsciByGovernorate"),

    data: fsciGovernorates.value.map((x) => Number(x.avg_fsci || 0)),

    labels: fsciGovernorates.value.map(
      (x) =>
        (isArabic.value
          ? x.governorate_name_ar || x.governorate_name_en
          : x.governorate_name_en || x.governorate_name_ar) || tr("unspecified")
    ),
  },

  "INF-05": {
    type: "bar",

    label: tr("equipmentByFacility"),

    data: (inf05Equipment.value.by_governorate || []).map((x) =>
      Number(x.INF_05 || 0)
    ),

    labels: (inf05Equipment.value.by_governorate || []).map(
      (x) =>
        (isArabic.value
          ? x.governorate_name_ar || x.governorate_name_en
          : x.governorate_name_en || x.governorate_name_ar) || tr("unspecified")
    ),
  },

  "INF-07": {
    type: "radialBar",

    label: tr("electricityAverage"),

    value: Number(
      inf07Electricity.value.summary?.INF_07 ??
        inf07Electricity.value.summary?.average ??
        0
    ),
  },

  "INF-08": {
    type: "bar",

    horizontal: true,

    label: tr("energyReliabilityByFacility"),

    data: (inf08Energy.value.components || []).map((x) =>
      Number(x.percent || 0)
    ),

    labels: (inf08Energy.value.components || []).map(
      (x) => x.power_source || tr("unspecified")
    ),
  },

  "INF-10": {
    type: "bar",

    horizontal: true,

    label: tr("disabilityAccessComponents"),

    data: (inf10Access.value.data || []).map((x) => Number(x.avg100 || 0)),

    labels: (inf10Access.value.data || []).map(
      (x) => x.point || tr("unspecified")
    ),
  },

  "INF-11": {
    type: "radar",

    label: tr("womenSafetyDimensions"),

    data: (womenSafetyPrivacy.value.data || []).map((x) =>
      Number(x.avg100 || 0)
    ),

    labels: (womenSafetyPrivacy.value.data || []).map(
      (x) => x.dimension || tr("unspecified")
    ),
  },
}));

const infraKpis = computed(() => [
  {
    code: "INF-01",

    name: tr("structuralConditionIndex"),

    val: Number(
      fsciIndicator.value.summary?.avg_fsci ||
        fsciGovernorates.value?.[0]?.avg_fsci ||
        0
    ).toFixed(2),

    unit: "/5",

    std: 2,

    dir: "lt",

    max: 5,

    composite: true,

    flag: false,

    chart: infraCharts.value["INF-01"],
  },

  {
    code: "INF-05",

    name: tr("medicalEquipmentFunctionality"),

    val: Number(inf05Equipment.value.summary?.INF_05 || 0).toFixed(1),

    unit: "%",

    std: 80,

    dir: "gt",

    max: 100,

    composite: true,

    flag: false,

    chart: infraCharts.value["INF-05"],
  },

  {
    code: "INF-07",

    name: tr("electricityAvailability"),

    val: Number(inf07Electricity.value.summary?.INF_07 || 0).toFixed(1),

    unit: "%",

    std: 75,

    dir: "gt",

    max: 100,

    composite: false,

    flag: false,

    chart: infraCharts.value["INF-07"],
  },

  {
    code: "INF-08",

    name: tr("energyReliabilityIndex"),

    val: Number(inf08Energy.value.summary?.INF_08 || 0).toFixed(1),

    unit: "/100",

    std: 67,

    dir: "gt",

    max: 100,

    composite: true,

    flag: false,

    chart: infraCharts.value["INF-08"],
  },

  {
    code: "INF-10",

    name: tr("disabilityAccessIndex"),

    val: Number(inf10Access.value.summary?.INF_10 || 0).toFixed(1),

    unit: "/100",

    std: 60,

    dir: "gt",

    max: 100,

    composite: true,

    flag: false,

    chart: infraCharts.value["INF-10"],
  },

  {
    code: "INF-11",

    name: tr("womenSafetyPrivacyIndex"),

    val: Number(womenSafetyPrivacy.value.summary?.INF_11 || 0).toFixed(1),

    unit: "/100",

    std: 70,

    dir: "gt",

    max: 100,

    composite: true,

    flag: true,

    chart: infraCharts.value["INF-11"],
  },
]);

const kpiStatus = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 0);

  if (kpi.dir === "lt") {
    if (v <= std) return "g";

    // حرج عندما تتجاوز القيمة المعيار بنسبة 30% أو أكثر.
    if (v < std * 1.3) return "a";

    return "r";
  }

  if (v >= std) return "g";

  // حرج عندما تقل القيمة عن المعيار بنسبة 30% أو أكثر.
  if (v > std * 0.7) return "a";

  return "r";
};

const statusColor = (kpi) => colors[kpiStatus(kpi)];

const achieve = (kpi) => {
  const v = Number(kpi.val || 0);

  const std = Number(kpi.std || 1);

  const max = Number(kpi.max || 100);

  if (kpi.dir === "lt") {
    return v <= std
      ? 100
      : Math.max(
          0,

          Math.round(100 - ((v - std) / Math.max(1, max - std)) * 100)
        );
  }

  return Math.min(100, Math.round((v / std) * 100));
};

const barWidth = (kpi) =>
  Math.min(
    100,

    Math.max(3, (Number(kpi.val || 0) / Number(kpi.max || 100)) * 100)
  );

const thresholdStyle = (kpi) => {
  const pos = Math.min(
    100,

    (Number(kpi.std || 0) / Number(kpi.max || 100)) * 100
  );

  return kpi.dir === "lt"
    ? { insetInlineStart: `${pos}%` }
    : { insetInlineStart: `${pos}%` };
};

const infraCriticalCount = computed(
  () => infraKpis.value.filter((k) => kpiStatus(k) === "r").length
);

const miniBarOptions = (labels = [], horizontal = false) => ({
  ...miniBaseChart,

  plotOptions: {
    bar: {
      horizontal,

      borderRadius: 6,

      columnWidth: "45%",
    },
  },

  xaxis: {
    categories: labels.length ? labels : [tr("noData")],

    labels: { rotate: horizontal ? 0 : -35, trim: true },
  },
});

const miniDonutOptions = (labels = []) => ({
  ...miniBaseChart,

  labels: labels.length ? labels : [tr("noData")],
});

const radarOptions = (labels = []) => ({
  ...miniBaseChart,

  chart: {
    toolbar: { show: false },

    fontFamily: "Cairo",

    foreColor: "#7890ad",
  },

  labels: labels.length ? labels : [tr("noData")],

  yaxis: {
    min: 0,

    max: 100,

    tickAmount: 5,

    labels: {
      formatter: (val) => Math.round(val),
    },
  },

  stroke: {
    width: 3,
  },

  fill: {
    opacity: 0.25,
  },

  markers: {
    size: 4,
  },

  dataLabels: {
    enabled: false,
  },

  legend: {
    show: false,
  },
});

const radialOptions = (value) => ({
  chart: {
    type: "radialBar",

    toolbar: {
      show: false,
    },

    fontFamily: "Cairo",
  },

  plotOptions: {
    radialBar: {
      startAngle: -135,

      endAngle: 135,

      hollow: {
        size: "62%",
      },

      track: {
        background: "#1f2a44",
      },

      dataLabels: {
        name: {
          show: false,
        },

        value: {
          fontSize: "34px",

          fontWeight: 800,

          color: "#f59e0b",

          formatter(val) {
            return val.toFixed(1) + "%";
          },
        },
      },
    },
  },

  colors: [value >= 75 ? "#10b981" : value >= 50 ? "#f59e0b" : "#ef4444"],

  labels: [tr("electricityLabel")],
});

const kpiApex = (kpi) => {
  const ch = kpi.chart;

  if (!ch) {
    return {
      type: "bar",

      series: [],

      options: miniBarOptions([]),
    };
  }

  if (ch.type === "donut") {
    return {
      type: "donut",

      series: ch.data?.length ? ch.data : [0],

      options: miniDonutOptions(ch.labels || []),
    };
  }

  if (ch.type === "radialBar") {
    const value = Number(ch.value || 0);

    return {
      type: "radialBar",

      series: [value],

      options: radialOptions(value),
    };
  }

  if (ch.type === "radar") {
    return {
      type: "radar",

      series: [
        {
          name: kpi.code,

          data: ch.data?.length ? ch.data : [0],
        },
      ],

      options: radarOptions(ch.labels || []),
    };
  }

  return {
    type: "bar",

    series: [
      {
        name: kpi.code,

        data: ch.data?.length ? ch.data : [0],
      },
    ],

    options: miniBarOptions(ch.labels || [], !!ch.horizontal),
  };
};
</script>

<style scoped>
.infra-page {
  min-height: 100vh;

  background: #f4f6f9;

  padding: 24px;

  color: #071126;

  font-family: "Cairo";
}

.page-loading {
  position: fixed;

  inset: 0;

  background: rgba(244, 246, 249, 0.92);

  backdrop-filter: blur(6px);

  z-index: 9999;

  display: flex;

  align-items: center;

  justify-content: center;
}

.loader-card {
  width: 320px;

  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 34px 24px;

  text-align: center;

  box-shadow: 0 20px 60px rgba(15, 35, 64, 0.15);
}

.loader-ring {
  width: 64px;

  height: 64px;

  border: 6px solid #e2e8f0;

  border-top-color: #143a63;

  border-radius: 50%;

  margin: 0 auto 18px;

  animation: spin 0.8s linear infinite;
}

.loader-card h3 {
  font-size: 18px;

  font-weight: 900;

  color: #143a63;

  margin-bottom: 8px;
}

.loader-card p {
  color: #64748b;

  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-header {
  margin-bottom: 22px;
}

.page-header h1 {
  font-size: 28px;

  font-weight: 900;

  color: #061226;

  margin-bottom: 8px;
}

.page-header p {
  color: #52657d;

  font-size: 14px;
}

.axis-block {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 22px;

  padding: 20px;

  margin-bottom: 22px;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.axis-head {
  display: flex;

  align-items: center;

  gap: 13px;

  border-bottom: 1px solid #e2e8f0;

  padding-bottom: 15px;

  margin-bottom: 15px;
}

.axis-no {
  width: 46px;

  height: 46px;

  border-radius: 14px;

  background: #0ea5e9;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #fff;

  font-weight: 900;
}

.axis-head h2 {
  font-size: 20px;

  font-weight: 900;

  color: #061226;

  margin: 0 0 5px;
}

.axis-head small {
  color: #64748b;

  font-size: 12px;
}

.axis-head p {
  color: #64748b;

  margin: 0;
}

.axis-weight {
  margin-inline-start: auto;

  text-align: center;

  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 9px 16px;
}

.axis-weight strong {
  display: block;

  font-size: 24px;

  color: #143a63;
}

.axis-weight span {
  font-size: 10px;

  color: #64748b;

  font-weight: 800;
}

.axis-meta {
  display: flex;

  gap: 8px;

  flex-wrap: wrap;

  margin-bottom: 16px;
}

.axis-meta span {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 999px;

  padding: 6px 12px;

  color: #64748b;

  font-size: 12px;

  font-weight: 700;
}

.indicator-grid {
  display: grid;

  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));

  gap: 14px;
}

.indicator-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 16px;

  position: relative;

  transition: 0.18s ease;

  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.indicator-card.flag {
  border-color: rgba(239, 68, 68, 0.38);
}

.indicator-card.flag::after {
  content: attr(data-critical-label);

  position: absolute;

  top: 13px;

  left: 14px;

  font-size: 10px;

  color: #ef4444;

  font-weight: 900;
}

.indicator-top {
  display: flex;

  gap: 8px;

  margin-bottom: 10px;
}

.code {
  direction: ltr;

  background: #eff6ff;

  border: 1px solid #bfdbfe;

  color: #143a63;

  border-radius: 7px;

  padding: 3px 8px;

  font-weight: 900;

  font-size: 11px;
}

.composite {
  background: #f5f3ff;

  border: 1px solid #ddd6fe;

  color: #7c3aed;

  border-radius: 7px;

  padding: 3px 8px;

  font-size: 10px;

  font-weight: 900;
}

.indicator-card h3 {
  font-size: 15px;

  font-weight: 900;

  color: #071126;

  margin: 0 0 12px;

  min-height: 42px;
}

.indicator-value {
  display: flex;

  align-items: baseline;

  gap: 7px;

  margin-bottom: 10px;
}

.indicator-value strong {
  font-size: 32px;

  font-weight: 900;

  direction: ltr;
}

.indicator-value span {
  color: #64748b;
}

.indicator-value em {
  margin-inline-start: auto;

  font-style: normal;

  background: #f8fafc;

  border: 1px solid #e2e8f0;

  border-radius: 8px;

  padding: 5px 9px;

  color: #475569;

  font-size: 11px;

  font-weight: 800;
}

.gauge {
  height: 9px;

  background: #e2e8f0;

  border-radius: 999px;

  position: relative;

  overflow: hidden;
}

.gauge div {
  height: 100%;

  border-radius: 999px;
}

.gauge i {
  position: absolute;

  top: -3px;

  bottom: -3px;

  width: 2px;

  background: #334155;

  opacity: 0.45;
}

.indicator-foot {
  display: flex;

  justify-content: space-between;

  align-items: center;

  margin: 8px 0 13px;

  font-size: 12px;

  font-weight: 700;

  color: #64748b;
}

.rag {
  border-radius: 7px;

  padding: 4px 10px;

  font-weight: 900;
}

.rag.g {
  background: rgba(16, 185, 129, 0.12);

  color: #10b981;
}

.rag.a {
  background: rgba(245, 158, 11, 0.14);

  color: #f59e0b;
}

.rag.r {
  background: rgba(239, 68, 68, 0.12);

  color: #ef4444;
}

.mini-chart {
  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  padding: 14px;
}

.mini-chart h4 {
  font-size: 14px;

  font-weight: 900;

  color: #143a63;

  margin: 0 0 10px;
}

.kpi-grid {
  display: grid;

  grid-template-columns: repeat(4, 1fr);

  gap: 14px;

  margin-bottom: 18px;
}

.kpi-card {
  border-radius: 18px;

  padding: 22px;

  text-align: center;

  border: 1px solid #dbe3ee;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.06);
}

.kpi-value {
  font-size: 30px;

  font-weight: 900;
}

.kpi-label {
  margin-top: 8px;

  font-size: 13px;

  font-weight: 800;
}

.kpi-card.blue {
  background: #eef2ff;

  color: #3949ab;
}

.kpi-card.red {
  background: #fdecea;

  color: #c62828;
}

.kpi-card.orange {
  background: #fff4e5;

  color: #d4821a;
}

.kpi-card.green {
  background: #e8f5e9;

  color: #1b5e20;
}

.section-title {
  font-size: 16px;

  font-weight: 900;

  color: #143a63;

  margin-bottom: 12px;
}

.charts-grid {
  display: grid;

  grid-template-columns: repeat(2, 1fr);

  gap: 14px;

  margin-bottom: 18px;
}

.chart-card {
  background: #fff;

  border: 1px solid #dbe3ee;

  border-radius: 16px;

  padding: 18px;

  min-height: 370px;

  max-width: 100%;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.table-wrap {
  overflow-x: auto;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  margin-top: 18px;

  background: #fff;

  box-shadow: 0 8px 24px rgba(15, 35, 64, 0.05);
}

.table-title {
  background: #143a63;

  color: #fff;

  padding: 15px 18px;

  font-weight: 900;

  display: flex;

  justify-content: space-between;

  align-items: center;
}

.table-title span {
  background: rgba(255, 255, 255, 0.16);

  padding: 4px 10px;

  border-radius: 999px;

  font-size: 12px;
}

table {
  width: 100%;

  min-width: 1100px;

  border-collapse: collapse;
}

th {
  background: #f8fafc;

  color: #143a63;

  font-weight: 900;

  padding: 13px;

  border-bottom: 1px solid #e2e8f0;

  white-space: wrap;

  text-align: start;
}

td {
  padding: 13px;

  border-bottom: 1px solid #eef2f7;

  white-space: wrap;

  font-size: 13px;
}

tbody tr:hover {
  background: #f8fafc;
}

.name {
  font-weight: 900;

  color: #071126;
}

.fis {
  font-weight: 900;

  color: #143a63;

  direction: ltr;
}

.level-badge {
  display: inline-flex;

  padding: 5px 12px;

  border-radius: 999px;

  font-size: 12px;

  font-weight: 900;
}

.level-green {
  background: #dcfce7;

  color: #166534;
}

.level-orange {
  background: #ffedd5;

  color: #9a3412;
}

.level-red {
  background: #fee2e2;

  color: #991b1b;
}

.level-gray {
  background: #f1f5f9;

  color: #475569;
}

.empty-cell {
  text-align: center;

  color: #64748b;

  padding: 20px !important;
}

.infra-page[dir="ltr"] .indicator-card.flag::after {
  left: auto;

  right: 14px;
}

.infra-page[dir="ltr"] {
  font-family: "Cairo", Arial, sans-serif;
}

.infra-page[dir="ltr"] .fis,
.infra-page[dir="ltr"] .code,
.infra-page[dir="ltr"] .indicator-value strong {
  direction: ltr;
}

@media (max-width: 1000px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .axis-head {
    flex-direction: column;

    align-items: stretch;
  }

  .axis-weight {
    margin-inline-start: 0;
  }
}

@media (max-width: 600px) {
  .infra-page {
    padding: 14px;
  }

  .kpi-grid,
  .indicator-grid {
    grid-template-columns: 1fr;
  }

  .page-header h1 {
    font-size: 22px;
  }
}

@media (max-width: 600px) {
  .infra-page {
    padding: 10px;
  }

  .page-header {
    margin-bottom: 14px;
  }

  .page-header h1 {
    font-size: 21px;

    line-height: 1.4;
  }

  .page-header p {
    font-size: 13px;

    line-height: 1.7;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    gap: 9px;
  }

  .kpi-card {
    padding: 14px 8px;

    border-radius: 14px;
  }

  .kpi-value {
    font-size: 24px;
  }

  .kpi-label {
    font-size: 11px;

    line-height: 1.5;
  }

  .axis-block {
    padding: 12px;

    border-radius: 16px;
  }

  .axis-head {
    gap: 10px;
  }

  .axis-head h2 {
    font-size: 17px;
  }

  .axis-weight {
    width: 100%;
  }

  .axis-meta span {
    flex: 1 1 calc(50% - 8px);

    text-align: center;
  }

  .indicator-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .indicator-card {
    padding: 13px;
  }

  .indicator-value {
    flex-wrap: wrap;
  }

  .indicator-value strong {
    font-size: 28px;
  }

  .indicator-value em {
    margin-inline-start: 0;

    width: 100%;

    text-align: center;
  }

  .mini-chart {
    padding: 8px;

    overflow: hidden;
  }

  .chart-card {
    padding: 12px;

    min-height: 330px;

    overflow: hidden;
  }

  .table-wrap {
    overflow: visible;

    border: 0;

    background: transparent;

    box-shadow: none;
  }

  .table-title {
    border-radius: 14px;

    margin-bottom: 10px;
  }

  table,
  thead,
  tbody,
  tr,
  th,
  td {
    display: block;

    width: 100%;
  }

  table {
    min-width: 0;
  }

  thead {
    display: none;
  }

  tbody {
    display: grid;

    gap: 12px;
  }

  tbody tr {
    background: #fff;

    border: 1px solid #dbe3ee;

    border-radius: 14px;

    padding: 8px 12px;

    box-shadow: 0 6px 18px rgba(15, 35, 64, 0.05);
  }

  tbody tr:hover {
    background: #fff;
  }

  td {
    display: flex;

    justify-content: space-between;

    align-items: center;

    gap: 14px;

    padding: 9px 0;

    border-bottom: 1px dashed #e2e8f0;

    white-space: normal;

    text-align: end;
  }

  td:last-child {
    border-bottom: 0;
  }

  td::before {
    content: attr(data-label);

    flex: 0 0 44%;

    color: #64748b;

    font-size: 11px;

    font-weight: 800;

    text-align: start;
  }

  .name {
    color: #071126;
  }

  .empty-cell {
    display: block;

    text-align: center;
  }

  .empty-cell::before {
    display: none;
  }

  .loader-card {
    width: calc(100% - 28px);
  }
}

@media (max-width: 360px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .axis-meta span {
    flex-basis: 100%;
  }
}

/* Axis summary layout matching the requested design */

.axis-summary {
  margin-bottom: 20px;
}

.axis-summary-head {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 24px;

  margin-bottom: 20px;
}

.axis-title {
  flex: 1;

  min-width: 0;

  text-align: start;
}

.axis-title h2 {
  margin: 0 0 8px;

  color: #087b72;

  font-size: 27px;

  line-height: 1.4;

  font-weight: 900;
}

.axis-title h2 small {
  color: #087b72;

  font-size: 17px;

  font-weight: 900;
}

.axis-title p {
  margin: 0;

  color: #5f7191;

  font-size: 15px;

  line-height: 1.8;
}

.axis-summary .axis-weight {
  flex: 0 0 164px;

  min-height: 140px;

  margin-inline-start: 0;

  padding: 20px 14px;

  border: 0;

  border-radius: 22px;

  background: #e9faf4;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  text-align: center;
}

.axis-summary .axis-weight strong {
  display: block;

  margin-bottom: 16px;

  color: #087b72;

  font-size: 39px;

  line-height: 1;

  font-weight: 900;

  direction: ltr;
}

.axis-summary .axis-weight small {
  color: #087b72;

  font-size: 13px;

  font-weight: 900;
}

.axis-statistics {
  display: grid;

  grid-template-columns: repeat(3, minmax(0, 1fr));

  gap: 14px;
}

.axis-stat-card {
  min-height: 116px;

  padding: 20px 16px;

  background: #f8fafc;

  border: 1px solid #dbe3ee;

  border-radius: 18px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  text-align: center;
}

.axis-stat-card strong {
  display: block;

  margin-bottom: 14px;

  color: #087b72;

  font-size: 31px;

  line-height: 1;

  font-weight: 900;
}

.axis-stat-card span {
  color: #5f7191;

  font-size: 16px;

  font-weight: 800;
}

@media (max-width: 700px) {
  .axis-summary-head {
    flex-direction: column;

    align-items: stretch;
  }

  .axis-summary .axis-weight {
    flex-basis: auto;

    width: 100%;

    min-height: 120px;
  }

  .axis-statistics {
    grid-template-columns: 1fr;
  }

  .axis-title h2 {
    font-size: 22px;
  }

  .axis-title p {
    font-size: 13px;
  }

  .axis-stat-card {
    min-height: 100px;
  }
}
</style>
